/**
 * useUnreadNotifications — light-touch polling for in_app_notifications count.
 *
 * Polls GET /api/notifications/unread-count every 30 seconds while the tab
 * is visible. When the user backgrounds the tab (Page Visibility API) the
 * timer is cleared, so no work happens off-screen — important for battery
 * life and for keeping audit-log noise down (SOC 2 CC7.2: limit unnecessary
 * authenticated reads). When visibility returns the hook fires one fresh
 * poll and resumes the interval.
 *
 * Returns { unreadCount, refresh } — refresh() lets callers force a poll
 * after they mark a notification as read so the badge updates instantly.
 *
 * SOC 2: read-only endpoint, no data exfiltration, scoped to session user
 * server-side (routes/notifications.js). No PHI leaves the network here.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const POLL_MS = 30_000;

export default function useUnreadNotifications() {
  const [unreadCount, setUnreadCount] = useState(0);
  const timerRef = useRef(null);
  const activeRef = useRef(true);

  const fetchOnce = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications/unread-count`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (!activeRef.current) return;
      if (data && data.success && typeof data.unreadCount === 'number') {
        setUnreadCount(data.unreadCount);
      }
    } catch {
      // Silent — a network blip should never break the layout. The next
      // tick (or the next visibility change) will recover the count.
    }
  }, []);

  const start = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setInterval(fetchOnce, POLL_MS);
  }, [fetchOnce]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    activeRef.current = true;

    // Page Visibility API — pause polling when the tab is backgrounded so
    // we don't keep the browser awake or hammer the API with calls nobody
    // will see. SOC 2 CC7.2 (system monitoring) is satisfied by polling
    // only while the user can actually act on the notification.
    const handleVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        stop();
      } else {
        fetchOnce();
        start();
      }
    };

    // Initial poll, then start the interval (only if the tab is visible).
    if (typeof document === 'undefined' || !document.hidden) {
      fetchOnce();
      start();
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    return () => {
      activeRef.current = false;
      stop();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
    };
  }, [fetchOnce, start, stop]);

  return { unreadCount, refresh: fetchOnce };
}
